import { requestStarted, requestError, requestComplete } from '../../core/actions/http-request';
import { removeSessionId } from '../utils/storage';
import { handleSessionId, modifyUrlAfterTokenValidation } from '../utils/waiting-room-view-functions';
import {
    fetchWaitingRooms, fetchWaitingRoom, upsertWaitingRoom, deleteWaitingRoom, startWaitingRoom, stopWaitingRoom,
    fetchWaitingRoomSessionPosition, fetchWaitingRoomChart, fetchWaitingRoomStats,
    fetchReturnInfo, verifyToken, addAgainInWaitingRoom,
} from '../api/cloudfare-worker-api';
import {
    GET_WAITING_ROOMS_REQUEST_NAME, UPSERT_WAITING_ROOM_REQUEST_NAME, DELETE_WAITING_ROOM_REQUEST_NAME,
    START_WAITING_ROOM_REQUEST_NAME, STOP_WAITING_ROOM_REQUEST_NAME, GET_WAITING_ROOM_SESSION_POSITION_REQUEST_NAME,
    GET_WAITING_ROOM_CHART_REQUEST_NAME, SESSION_PROCESSED_VALUE, SESSION_EXPIRED_VALUE,
} from '../api/config';

export const SET_WAITING_ROOMS = 'SET_WAITING_ROOMS';
export const setWaitingRooms = (payload) => ({ type: SET_WAITING_ROOMS, payload });

export const EDIT_WAITING_ROOM = 'EDIT_WAITING_ROOM';
export const editWaitingRoom = (id, fieldsNames, values) => ({ type: EDIT_WAITING_ROOM, id, fieldsNames, values });

export const DELETE_WAITING_ROOM = 'DELETE_WAITING_ROOM';
export const doDeleteWaitingRoom = (id) => ({ type: DELETE_WAITING_ROOM, id });

export const SET_WAITING_ROOM_CHART = 'SET_WAITING_ROOM_CHART';
export const setWaitingRoomChart = (id, chartData) => ({ type: SET_WAITING_ROOM_CHART, id, chartData });

export const SET_CREATE_WAITING_ROOM_FORM_FIELD = 'SET_CREATE_WAITING_ROOM_FORM_FIELD';
export const setCreateWaitingRoomFormField = (fieldName, value, isValid) => ({
    type: SET_CREATE_WAITING_ROOM_FORM_FIELD, fieldName, value, isValid,
});

export const SET_WAITING_ROOM_VIEW_INFO = 'SET_WAITING_ROOM_VIEW_INFO';
export const setWaitingRoomViewInfo = (payload) => ({ type: SET_WAITING_ROOM_VIEW_INFO, payload });

export const RESET_CREATE_WAITING_ROOM_FORM = 'RESET_CREATE_WAITING_ROOM_FORM';
export const resetCreateWaitingRoomForm = () => ({ type: RESET_CREATE_WAITING_ROOM_FORM });

export const getWaitingRoomStats = (waitingRoomId) => async (dispatch) => {
    try {
        dispatch(editWaitingRoom(waitingRoomId, ['analyticsLoading', 'analyticsError'], [true, false]));
        const stats = await fetchWaitingRoomStats(waitingRoomId);
        dispatch(editWaitingRoom(waitingRoomId, ['analyticsData', 'analyticsLoading'], [stats, false]));
    } catch (e) {
        dispatch(editWaitingRoom(waitingRoomId, ['analyticsLoading', 'analyticsError'], [false, true]));
    }
};

export const getWaitingRooms = (setHasAccess, page = 1, searchTerm = '') => async (dispatch) => {
    try {
        dispatch(requestStarted(GET_WAITING_ROOMS_REQUEST_NAME));
        const waitingRooms = await fetchWaitingRooms(page, searchTerm);
        const waitingRoomsWithLoading = waitingRooms.map((room) => ({
            ...room,
            analyticsLoading: true,
            analyticsError: false,
            analyticsData: null,
        }));
        dispatch(setWaitingRooms(waitingRoomsWithLoading));
        waitingRoomsWithLoading.forEach((room) => {
            dispatch(getWaitingRoomStats(room.id));
        });
    } catch (e) {
        if (e.message === 'Unauthorized') {
            setHasAccess(false);
            return;
        }

        dispatch(requestError(GET_WAITING_ROOMS_REQUEST_NAME));
    } finally {
        dispatch(requestComplete(GET_WAITING_ROOMS_REQUEST_NAME));
    }
};

export const getWaitingRoom = (waitingRoomId) => async (dispatch) => {
    try {
        dispatch(requestStarted(GET_WAITING_ROOMS_REQUEST_NAME));
        const waitingRoom = await fetchWaitingRoom(waitingRoomId);
        dispatch(setWaitingRooms([{
            ...waitingRoom,
            analyticsLoading: true,
            analyticsError: false,
            analyticsData: null,
        }]));
        dispatch(getWaitingRoomStats(waitingRoom.id));
    } catch (e) {
        dispatch(requestError(GET_WAITING_ROOMS_REQUEST_NAME));
    } finally {
        dispatch(requestComplete(GET_WAITING_ROOMS_REQUEST_NAME));
    }
};

export const doUpsertWaitingRoom = (payload) => async (dispatch) => {
    try {
        dispatch(requestStarted(UPSERT_WAITING_ROOM_REQUEST_NAME));
        await upsertWaitingRoom(payload);
        dispatch(getWaitingRooms());
    } catch (e) {
        dispatch(requestError(UPSERT_WAITING_ROOM_REQUEST_NAME));
    } finally {
        dispatch(requestComplete(UPSERT_WAITING_ROOM_REQUEST_NAME));
    }
};

export const deleteWaitingRoomAction = (id) => async (dispatch) => {
    try {
        dispatch(requestStarted(DELETE_WAITING_ROOM_REQUEST_NAME));
        await deleteWaitingRoom(id);
        dispatch(doDeleteWaitingRoom(id));
    } catch (e) {
        dispatch(requestError(DELETE_WAITING_ROOM_REQUEST_NAME));
    } finally {
        dispatch(requestComplete(DELETE_WAITING_ROOM_REQUEST_NAME));
    }
};

export const doStartWaitingRoom = (id) => async (dispatch) => {
    try {
        dispatch(requestStarted(START_WAITING_ROOM_REQUEST_NAME));
        await startWaitingRoom(id);
        dispatch(editWaitingRoom(id, ['status'], ['running']));
    } catch (e) {
        dispatch(requestError(START_WAITING_ROOM_REQUEST_NAME));
    } finally {
        dispatch(requestComplete(START_WAITING_ROOM_REQUEST_NAME));
    }
};

export const doStopWaitingRoom = (id) => async (dispatch) => {
    try {
        dispatch(requestStarted(STOP_WAITING_ROOM_REQUEST_NAME));
        await stopWaitingRoom(id);
        dispatch(editWaitingRoom(id, ['status'], ['stopped']));
    } catch (e) {
        dispatch(requestError(STOP_WAITING_ROOM_REQUEST_NAME));
    } finally {
        dispatch(requestComplete(STOP_WAITING_ROOM_REQUEST_NAME));
    }
};

export const getSessionPosition = (waitingRoomId, sessionId) => async (dispatch) => {
    try {
        dispatch(requestStarted(GET_WAITING_ROOM_SESSION_POSITION_REQUEST_NAME));
        const response = await fetchWaitingRoomSessionPosition(waitingRoomId, sessionId);
        dispatch(setWaitingRoomViewInfo(response));

        const { position } = response;
        if (position === SESSION_PROCESSED_VALUE) {
            const { token, returnUrl } = await fetchReturnInfo(waitingRoomId, sessionId);
            const url = new URL(returnUrl);
            url.searchParams.append('ibqueuetoken', token);
            window.location.href = url.toString();
        } if (position === SESSION_EXPIRED_VALUE) {
            removeSessionId(waitingRoomId);
            const newSessionId = handleSessionId(waitingRoomId);
            await addAgainInWaitingRoom(waitingRoomId, sessionId, newSessionId);
            // eslint-disable-next-line
            window.location.reload();
        }
    } catch (e) {
        dispatch(requestError(GET_WAITING_ROOM_SESSION_POSITION_REQUEST_NAME));
    } finally {
        dispatch(requestComplete(GET_WAITING_ROOM_SESSION_POSITION_REQUEST_NAME));
    }
};

export const doVerifyToken = (token, sessionId, setInitialLoad) => async (dispatch) => {
    try {
        dispatch(requestStarted(GET_WAITING_ROOMS_REQUEST_NAME));
        const response = await verifyToken(token, sessionId);
        if (response.message && response.message === 'Invalid token') {
            dispatch(requestError(GET_WAITING_ROOMS_REQUEST_NAME));
            return;
        }
        if (response.token) {
            const url = new URL(response.returnUrl);
            url.searchParams.append('ibqueuetoken', response.token);
            window.location.href = url.toString();
            return;
        }

        dispatch(setWaitingRooms([response]));
        modifyUrlAfterTokenValidation(response.id);
        setInitialLoad(true);
    } catch (e) {
        dispatch(requestError(GET_WAITING_ROOMS_REQUEST_NAME));
    } finally {
        dispatch(requestComplete(GET_WAITING_ROOMS_REQUEST_NAME));
    }
};

export const getWaitingRoomChart = (id, startDate, endDate, groupedBy) => async (dispatch) => {
    try {
        dispatch(requestStarted(GET_WAITING_ROOM_CHART_REQUEST_NAME));
        const chartData = await fetchWaitingRoomChart(id, startDate, endDate, groupedBy);
        dispatch(setWaitingRoomChart(id, chartData));
    } catch (e) {
        dispatch(requestError(GET_WAITING_ROOM_CHART_REQUEST_NAME));
    } finally {
        dispatch(requestComplete(GET_WAITING_ROOM_CHART_REQUEST_NAME));
    }
};
